import type {
    CallParams,
    ConfirmGasPriceParams,
    GasPolicy,
    SendParams,
    TransactionObject,
    Web3Provider,
} from "@/cuties/blockchain/matic/Web3Provider";
import type { Blockchain } from "@/cuties/model/pet/BlockchainId";
import type { TransactionHash } from "@/components/LoginManager/TypeDefs";
import type { TransactionReceipt } from "web3-core";
import Web3ContractRegistry from "@/cuties/blockchain/web3/Web3ContractRegistry";
import { toBigNumber } from "@/common/utils/toBigNumber";
import { ShowLoader } from "@/common/decorators/ShowLoader";
import type { BlockTransactionString } from "web3-eth";
import type Web3 from "web3";

export type Web3Confirmation = (confNumber: number, receipt: TransactionReceipt) => void;

/**
 * takes a web3 instance and some particular wallet storage provider and
 * provides few convenient functions to interact with blockchain
 */
export default class Web3ProviderAdapter {
    public readonly walletProvider: Web3Provider;
    private readonly blockchain: Blockchain;
    private whenRegistry: Promise<Web3ContractRegistry> | null = null;

    constructor(walletProvider: Web3Provider, blockchain: Blockchain) {
        this.walletProvider = walletProvider;
        this.blockchain = blockchain;
    }

    private async getLegacyGasPolicy(web3: Web3, block: BlockTransactionString): Promise<GasPolicy> {
        const gasPrice = await web3.eth.getGasPrice();
        const gasPriceBN = toBigNumber(gasPrice);

        // we don't have integration with gas stations,
        // let's make an assumption and make 'slow' cheaper and 'fast' more expensive
        return {
            type: "legacy",
            blockGasLimit: block.gasLimit,
            gasPrices: {
                Slow: gasPriceBN.multipliedBy(0.9).toString(),
                Normal: gasPrice,
                Fast: gasPriceBN.multipliedBy(1.1).toString(),
            },
        };
    }

    /**
     * Web3 blockchains have at least two GAS models.
     *
     * ## Legacy Gas Model
     * Legacy model is when gas unit has price known as 'gasPrice'.
     * Every network participant can set their own gasPrice for a transaction.
     * Higher gas price is more preferable for blockchain miner as
     * they receive 'tip' from paid gas with a certain gas price.
     * In the case when gas price is low, miners can ignore transaction
     * meanwhile higher 'gas price' transactions are present.
     *
     * External blockchain observers (GasStations) are looking into each block transactions and
     * accumulates gasPrice percentiles and afterward gives a forecast for efficient gas price.
     *
     * Legacy model is very unstable and low predictable, especially when the network is under load.
     *
     * ## EIP1559 (aka London fork) Gas Model
     * EIP1559 gas model is based on 'baseFee' when transaction gas price must not be lower than 'baseFee'.
     * The idea behind 'baseFee' is that it may change its value only in 12.5% between blocks that are considered more predictable.
     * However, when the amount of transactions is high the mining queue will arise.
     * The protocol has the option to set 'priorityFee' which will be taken by the miner as 'tip'.
     * The end gas price will be 'baseFee' + 'priorityFee' which leads to cases when transactions potentially can consume more
     * than expected and be expensive due to 'baseFee' changes.
     * To solve unexpected expenses protocol has 'maxFee' that is a hard cap for transaction gas price and is gas price limit.
     * Unlike legacy 'gasPrice' the 'maxFee' is only a cap, transaction could be mined with less gas price at the end.
     *
     * There is no more need for external observers to get actual priority values because the protocol has a method for lookup.
     *
     * @return GasPolicy with meaningful data for each model
     */
    public async getGasPolicy(): Promise<GasPolicy> {
        const web3 = await this.walletProvider.getWeb3();
        const block = await web3.eth.getBlock("latest");

        // block does not contain base fee field - legacy gas model
        if (!block.baseFeePerGas) {
            return this.getLegacyGasPolicy(web3, block);
        }

        // take last block and rewards with 25/50/75 percentiles - rewards comes from submitted priority fees
        let feeHistory;
        try {
            feeHistory = await web3.eth.getFeeHistory(1, "latest", [25, 50, 75]);
        } catch (error) {
            if (error === true) {
                // for some reason Wombat Desktop always throws `true` boolean value as error
                // on any call to getFeeHistory(), probably that means "Not Supported"
                return this.getLegacyGasPolicy(web3, block);
            } else {
                throw error;
            }
        }
        const [slow, normal, fast] = (feeHistory.reward[0] ?? []).map((fee) => Number(fee).toString());

        const baseFee = Math.max(Number(block.baseFeePerGas), Number(feeHistory.baseFeePerGas[0] ?? 0)).toString();

        return {
            type: "eip1559",
            blockGasLimit: block.gasLimit,
            baseFee,
            gasPrices: { Slow: slow, Normal: normal, Fast: fast },
        };
    }

    private getRegistry(): Promise<Web3ContractRegistry> {
        if (!this.whenRegistry) {
            this.whenRegistry = this.walletProvider.getWeb3()
                .then(web3 => new Web3ContractRegistry(web3, this.blockchain));
        }
        return this.whenRegistry;
    }

    private async getMethodObj(callParams: CallParams): Promise<TransactionObject> {
        const registry = await this.getRegistry();
        return registry.getMethodObj(callParams);
    }

    /**
     * invoke a constant (read-only) method
     *
     * @return - method-specific data format
     */
    public async callContractMethod<T>(callParams: CallParams): Promise<T> {
        await this.walletProvider.checkReadyForTransaction();

        const transactionObj = await this.getMethodObj(callParams);

        return transactionObj.call();
    }

    /**
     * Send native blockchain asset from user to target address
     * @param to hex address
     * @param amount human readable
     */
    public async sendAsset(to: string, amount: string): Promise<TransactionHash> {
        const web3 = await this.walletProvider.getWeb3();

        const from = await this.walletProvider.getWalletAddress();
        const value = web3.utils.toWei(amount, "ether");
        const gas = await web3.eth.estimateGas({ to, from, value });

        const gasPolicy = await this.getGasPolicy();

        const confirmedGas = await this.walletProvider.confirmGasPrice({
            sendOptions: { from, value, gas },
            gasPolicy,
            to,
        });

        const sending = web3.eth.sendTransaction({ to, from, value, ...confirmedGas });

        return new Promise<string>((resolve, reject) => {
            sending.on("transactionHash", resolve);
            sending.on("error", reject);
        });
    }

    /**
     * these calls to chainlayer take rather long, so we show a loader while they are taking place for good UX, as
     * otherwise user can't know whether nothing is happening because he has to wait more or because something is wrong
     *
     * public for admin page
     */
    @ShowLoader
    public async estimateMethodSend(sendParams: SendParams) {
        await this.walletProvider.checkReadyForTransaction();

        const web3 = await this.walletProvider.getWeb3();

        const whenTransactionObj = this.getMethodObj(sendParams);

        const from = await this.walletProvider.getWalletAddress();

        const methodOptions = { from, value: sendParams.value };

        // this approach is analogous to Promise.all()
        const whenTrxCnt = web3.eth.getTransactionCount(from, "pending");
        const whenGasEstimation = whenTransactionObj.then((m) => m.estimateGas(methodOptions));
        const whenGasPolicy = this.getGasPolicy();

        const transactionObj = await whenTransactionObj;

        const trxCnt = await whenTrxCnt;

        const gasEstimation = await whenGasEstimation;
        const gasPolicy = await whenGasPolicy;
        const sendOptions: ConfirmGasPriceParams["sendOptions"] = {
            ...methodOptions,
            gas: Math.round(gasEstimation * 1.2), // use web3 gas estimation but multiply to be safe
            nonce: trxCnt,
        };

        return { sendOptions, gasPolicy, transactionObj };
    }

    /**
     * invoke method that alters blockchain (and therefore costs gas)
     * @param sendParams - invocation transaction parameters like callData, value, etc.
     * @param onConfirmation - is callback fired on transaction confirmation
     * (by default web3 has 24 block for tx confirmation)
     */
    public async sendContractMethod(sendParams: SendParams, onConfirmation?: Web3Confirmation): Promise<TransactionHash> {
        const { sendOptions, gasPolicy, transactionObj } = await this.estimateMethodSend(sendParams);

        const to = transactionObj._parent["_address"];
        const confirmedGas = await this.walletProvider.confirmGasPrice({ sendOptions, gasPolicy, to });

        const sending = transactionObj.send({
            ...sendOptions,
            ...confirmedGas,
        });

        return new Promise<string>((resolve, reject) => {
            if (onConfirmation) sending.on("confirmation", onConfirmation);
            sending.on("transactionHash", resolve);
            sending.on("error", reject);
        });
    }

    public async estimateGas(sendParams: SendParams): Promise<number> {
        await this.walletProvider.checkReadyForTransaction();

        const transactionObj = await this.getMethodObj(sendParams);

        const from = await this.walletProvider.getWalletAddress();

        const methodOptions = { from, value: sendParams.value };

        return transactionObj.estimateGas(methodOptions);
    }

    public async getWalletAddress(): Promise<string> {
        return this.walletProvider.getWalletAddress();
    }
}
